/**
 * Finds every compiled catalogue in `packages/i18n/catalogues/` and loads ONE.
 *
 * # The file layout, and why it is not the runtime layout
 *
 * On disk there is one JSON file per namespace per locale:
 *
 *   catalogues/en/common.json     { "cancel": "Cancel" }
 *   catalogues/fr/common.json     { "cancel": "Annuler" }
 *
 * because that is the unit a person reviews and a translator is handed. One
 * 5,000-key file is not reviewable and a diff against it is not readable.
 *
 * At runtime the files for the resolved locale are merged into a single i18next
 * namespace, with each key prefixed by its filename:
 *
 *   { fr: { translation: { "common.cancel": "Annuler" } } }
 *
 * so that `t('common.cancel', 'Cancel')` addresses a key with one dotted string
 * and no caller ever has to name a namespace separately. It is the same key
 * shape `apps/marketing/app/components/UiCopyProvider.tsx` parses for storefront
 * copy.
 *
 * # One locale per visitor, not all of them
 *
 * The glob is LAZY. `import.meta.glob` without `eager` hands back a map of path
 * to an import thunk, so the KEYS are known at build time while the contents
 * stay in their own chunks. That is what makes {@link SUPPORTED_LOCALES}
 * synchronous — the language switcher needs the list, not the copy — while a
 * reader only ever downloads the language they are being served.
 *
 * It used to be `eager: true`, which inlined every catalogue into every app
 * bundle. That was the right call while English was the only one: one catalogue
 * is noise. At four it is 365 kB gzipped of copy that no single reader can use,
 * carried by `apps/auth` as well, which is pre-auth and first-paint critical.
 *
 * # English is never fetched
 *
 * Every call site is `t(key, 'The English')`, so the English catalogue is
 * redundant at runtime: an English reader renders from the
 * defaults already in the bundle and loads no catalogue at all. Only the other
 * languages cost a chunk, which is also why a chunk that fails to arrive
 * degrades to readable English rather than to blank or to raw keys.
 */

import type { Catalogues } from './provider';
import { DEFAULT_LOCALE } from './resolve';

/** The one piece of Vite's `ImportMeta` this package uses. See below. */
interface GlobbedImportMeta {
  glob: (
    pattern: string,
    options: { import: 'default' },
  ) => Record<string, () => Promise<Record<string, string>>>;
}

/** The single i18next namespace every catalogue file is merged into. */
export const RUNTIME_NAMESPACE = 'translation';

/**
 * Path to import thunk, for every catalogue file in the repo.
 *
 * Typed through a local cast rather than `vite/client`. Every consumer of this
 * package compiles its SOURCE, so pulling Vite's ambient types in here would
 * force `"types": ["vite/client"]` into the tsconfig of `@foir/auth`,
 * `@foir/ui` and every app besides.
 *
 * The cast has to stay wrapped around `import.meta` at the CALL, not hoisted to
 * a variable: Vite matches `import.meta.glob(...)` syntactically and rewrites it
 * at build time, and `const glob = import.meta.glob` is a member access it does
 * not recognise, which fails at runtime rather than at compile.
 */
function catalogueFiles(): Record<string, () => Promise<Record<string, string>>> {
  return (import.meta as unknown as GlobbedImportMeta).glob('../catalogues/*/*.json', {
    import: 'default',
  });
}

/** '../catalogues/fr/common.json' -> `{ locale: 'fr', namespace: 'common' }` */
function parsePath(path: string): { locale: string; namespace: string } | null {
  const match = /\/catalogues\/([^/]+)\/([^/]+)\.json$/.exec(path);
  return match ? { locale: match[1], namespace: match[2] } : null;
}

/**
 * Every locale that has a catalogue compiled into this build, `en` included.
 *
 * Known synchronously because only the glob's keys are read, never its thunks.
 * `LanguageSwitcher` renders nothing when this has one entry, so a build with
 * English alone still hides the control rather than offering a choice of one.
 *
 * Always contains {@link DEFAULT_LOCALE}, even in a build where the catalogues
 * directory is somehow empty: the resolver has to have something to match
 * against, and every string has an in-code English default behind it.
 */
export const SUPPORTED_LOCALES: readonly string[] = (() => {
  const locales = new Set<string>([DEFAULT_LOCALE]);
  for (const path of Object.keys(catalogueFiles())) {
    const parsed = parsePath(path);
    if (parsed) locales.add(parsed.locale);
  }
  return [...locales].sort();
})();

/**
 * Load the catalogue for ONE locale, shaped for i18next's `resources`.
 *
 * Returns `{}` for English and for a locale with no files: both render from the
 * in-code defaults, so there is nothing to fetch and nothing to wait for.
 *
 * The per-namespace chunks are requested together and merged once, so a locale
 * is one round of work rather than 46 sequential awaits.
 */
export async function loadCatalogue(locale: string): Promise<Catalogues> {
  if (locale === DEFAULT_LOCALE) return {};

  const wanted = Object.entries(catalogueFiles()).flatMap(([path, load]) => {
    const parsed = parsePath(path);
    return parsed?.locale === locale ? [{ namespace: parsed.namespace, load }] : [];
  });
  if (wanted.length === 0) return {};

  const loaded = await Promise.all(
    wanted.map(async ({ namespace, load }) => ({ namespace, entries: await load() })),
  );

  const target: Record<string, string> = {};
  for (const { namespace, entries } of loaded) {
    for (const [key, value] of Object.entries(entries)) {
      target[`${namespace}.${key}`] = value;
    }
  }
  return { [locale]: { [RUNTIME_NAMESPACE]: target } };
}
